'use client'

import { type FormEvent, useState } from 'react'
import { sendDealDesktopLink } from '@/lib/actions/submit-form'

import { Icon } from '@/icons/Icon'
import type { Deal, App } from '@/payload-types'

type DealDesktopProps = {
  deal: Deal
}

export default function DealDesktop({ deal }: DealDesktopProps) {
  const [linkCopied, setLinkCopied] = useState(false)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function handleEmailLinkSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status === 'sending' || status === 'sent') return

    setStatus('sending')
    setError(null)

    try {
      await sendDealDesktopLink({ dealId: deal.id, email })
      setStatus('sent')
    } catch (error) {
      setStatus('idle')
      setError(
        error instanceof Error ? error.message : "We couldn't send your link. Please try again.",
      )
    }
  }

  const app = deal.app as App

  async function copyLink() {
    const url = `https://efficient.deals/${app.slug}`
    try {
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      setLinkCopied(false)
    }
  }

  return (
    <div className="flex flex-col gap-10 px-6 py-8">
      <div className="flex flex-col gap-3 text-center">
        <h2 className="text-24 font-semibold text-gray-950 dark:text-white">
          Finish on desktop to get
          <br />
          {deal.name}
        </h2>
        <p className="text-15 leading-snug text-gray-600 dark:text-white/60">
          {app.name} requires this offer to be activated on a computer. Share this deal with
          yourself now, then open it on your desktop to continue.
        </p>
      </div>

      <div className="flex flex-col gap-3 text-14">
        <div className="flex h-11 items-center rounded-full border border-gray-200 p-1 dark:border-white/10">
          <span className="w-full px-4 text-gray-900 dark:text-white">
            efficient.deals/{app.slug}
          </span>
          <button
            onClick={copyLink}
            className="flex h-9 w-fit shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-3 font-semibold text-white"
          >
            <Icon name={linkCopied ? 'check' : 'clone'} />
            <span>{linkCopied ? 'Link copied' : 'Copy'}</span>
          </button>
        </div>

        <div className="flex h-4.5 items-center gap-2.5 text-gray-400">
          <hr className="flex-1 border-gray-200 dark:border-white/10" />
          <span className="text-12 uppercase text-gray-400 dark:text-white/40">or</span>
          <hr className="flex-1 border-gray-200 dark:border-white/10" />
        </div>

        <form
          onSubmit={handleEmailLinkSubmit}
          className="flex h-11 items-center rounded-full border border-gray-200 p-1 dark:border-white/10"
        >
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={status !== 'idle'}
            className="w-full px-4 text-gray-900 outline-none placeholder:text-gray-900/60 focus:outline-none disabled:bg-transparent dark:text-white dark:placeholder:text-white/40"
            placeholder="Your email address"
          />
          <button
            type="submit"
            disabled={status !== 'idle'}
            className="flex h-9 w-fit shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-3 font-semibold text-white"
          >
            {status === 'sent' && <Icon name="check" />}
            <span>
              {status === 'sending'
                ? 'Sending...'
                : status === 'sent'
                  ? 'Email sent'
                  : 'Email me the link'}
            </span>
          </button>
        </form>
        {error && <p className="text-center text-12 text-red-500">{error}</p>}
      </div>
    </div>
  )
}
